import React, { useState, useEffect, useCallback, useContext } from 'react';
import Question from "../../components/Question";
import { useNavigate, useLocation } from "react-router-dom";
import MoveButton from "../../components/MoveButton";
import ContinueButton from "../../components/ContinueButton";
import { Box, Typography, Skeleton, Grid, Divider } from "@mui/material";
import { useSelector, useDispatch } from 'react-redux';
import insurers from '../../assets/insurers.json';
import { reset as insuranceReset, setInsurer as updateInsurer } from "../../redux/insuranceSlice";
import { setPayMethod as updatePayMethod, setPaid as updatePaid } from "../../redux/userSlice";
import { reset as stripeReset } from "../../redux/stripeSlice";
import { continueButtonSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import { MinorUserContext } from "../../context/UserNameProvider";

const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

export default function Insurer() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { returning = false, insuranceChanged = false, onFile = false } = location.state || {};

    const user = useSelector((state) => state.user);
    const insurance = useSelector((state) => state.insurance);
    const { isMinor } = useContext(MinorUserContext);

    const [loadedImages, setLoadedImages] = useState({});
    const [visibleInsurers, setVisibleInsurers] = useState([]);

    const loadImage = useCallback(async (insurer, attempt = 0) => {
        try {
            const src = require(`../../assets/insure-logos/${insurer.replace(/\s+/g, "-").replace(/\//g, "")}.jpg`);
            await preloadImage(src);
            setLoadedImages(prev => ({ ...prev, [insurer]: src }));
        } catch (error) {
            // A cold CDN/edge cache miss right after a deploy can fail the first
            // fetch; retry once before giving up so we don't leave a permanent
            // skeleton (the name fallback renders if it still fails).
            if (attempt < 1) {
                setTimeout(() => loadImage(insurer, attempt + 1), 1500);
                return;
            }
            console.error(`Failed to load image for ${insurer}:`, error);
            // Mark as failed (false) so the UI shows the name instead of a
            // never-ending skeleton.
            setLoadedImages(prev => ({ ...prev, [insurer]: false }));
        }
    }, []);

    useEffect(() => {
        if (user.address.state && insurers[user.address.state]) {
            const stateInsurers = insurers[user.address.state];
            setVisibleInsurers(stateInsurers.slice(0, 6));
            stateInsurers.slice(0, 6).forEach(loadImage);
            setTimeout(() => {
                setVisibleInsurers(stateInsurers);
                stateInsurers.slice(6).forEach(loadImage);
            }, 1000);
        }
    }, [user.address.state, loadImage]);

    const getLogoStyle = (insurer) => {
        const baseStyle = {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
        };

        switch (insurer) {
            case "Anthem":
                return { ...baseStyle, height: "60px" };
            case "Aetna":
            case "United Healthcare":
                return { ...baseStyle, height: "50px" };
            case "Blue Cross Blue Shield":
                return { ...baseStyle, height: "80px" };
            default:
                return baseStyle;
        }
    };

    const renderInsurerButton = (insurer) => (
        <Grid item xs={6} md={4} key={insurer}>
            <MoveButton
                sx={{
                    height: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                }}
                onClick={() => {
                    if (user.payMethod === "self") {
                        dispatch(stripeReset());
                        dispatch(updatePaid(false));
                    }
                    dispatch(updatePayMethod("insurance"));
                    dispatch(updateInsurer(insurer));
                    if (returning) {
                        setPageAndDeferNavigate(dispatch, '/insurance-confirm', () =>
                            navigate('/insurance-confirm', { state: { returning, insuranceChanged, onFile } })
                        );
                    } else {
                        if (process.env.REACT_APP_OURS_EVENT) {
                            // eslint-disable-next-line no-undef
                            ours('track', 'InitiateCheckout');
                        }
                        setPageAndDeferNavigate(dispatch, '/insurance/method', () =>
                            navigate('/insurance/method', { state: { returning } })
                        );
                    }
                }}
                title={
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {loadedImages[insurer] === undefined ? (
                            <Skeleton variant="rectangular" width="80%" height="80%" />
                        ) : loadedImages[insurer] ? (
                            <img
                                src={loadedImages[insurer]}
                                alt={insurer}
                                style={getLogoStyle(insurer)}
                            />
                        ) : (
                            <Typography sx={{ fontWeight: 600, textAlign: "center" }}>
                                {insurer}
                            </Typography>
                        )}
                    </Box>
                }
                selected={insurance.insurer === insurer}
            />
        </Grid>
    );

    const handlePaySelf = () => {
        if (user.payMethod === "insurance") {
            dispatch(stripeReset());
            dispatch(updatePaid(false));
        }
        dispatch(updatePayMethod("self"));
        dispatch(insuranceReset());
        if (returning) {
            setPageAndDeferNavigate(dispatch, '/payment', () =>
                navigate('/payment', { state: { returning: true } })
            );
        } else {
            if (process.env.REACT_APP_OURS_EVENT) {
                // eslint-disable-next-line no-undef
                ours('track', 'InitiateCheckout');
            }
            setPageAndDeferNavigate(dispatch, '/whats-next', navigate);
        }
    };

    const handleBack = () => {
        if (returning) {
            setPageAndDeferNavigate(dispatch, '/insurance-options', () =>
                navigate('/insurance-options', { state: { returning } })
            );
        } else if (isMinor) {
            setPageAndDeferNavigate(dispatch, '/minor/instructions', navigate);
        } else {
            setPageAndDeferNavigate(dispatch, '/demographic', navigate);
        }
    };

    return (
        <Question
            onBack={handleBack}
            title={`How are you paying?`}
            returning={returning}
            input={
                <>
                    <Grid container spacing={2} sx={{ pl: { md: "20%" }, pr: { md: "20%" } }}>
                        {user.address.state && insurers[user.address.state] ?
                            visibleInsurers.map(renderInsurerButton)
                            :
                            <Typography sx={{ mt: "1rem" }}>
                                Please return to the address page and enter your state first.
                            </Typography>
                        }
                    </Grid>
                    {!returning && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', pt: 3 }}>
                                <Divider sx={{ flex: 1 }} />
                                <Typography variant="h7" color="secondary" sx={{
                                    px: 1, fontWeight: "normal",
                                }}>
                                    Or
                                </Typography>
                                <Divider sx={{ flex: 1 }} />
                            </Box><Box
                                sx={{ ...continueButtonSx, p: 0, pt: 4 }}
                            >
                                <ContinueButton
                                    onClick={handlePaySelf}
                                    title="I'm paying for myself"
                                    selected={user.payMethod === "self"} />
                            </Box>
                        </>
                    )}
                </>
            }
        />
    );
}